import React from "react";
import { NumberOfProblems } from "./NumberOfProblems.tsx";
import { InputNumber } from "primereact/inputnumber";
import { ToggleButton } from "primereact/togglebutton";
import { TOGGLE_PT } from "../../Shared/toggleButtonPt.ts";
import { useSetting, useSettingsActions } from "./SettingsStore.tsx";
import { ProblemFormatOptions } from "../Constants.ts";
import {showsWorkedAnswers, showsAllowNegatives, showsHalfPage, showsFactLevels, showsTimed } from "./AdvancedSettings.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * How much paper, and what is printed on it - in two halves since ADR 0061.
 *
 * `PaperSettings` is the rail's "Paper" section, and basic (ADR 0023 §2):
 * problems per page, number of pages, the answer key.
 *
 * `PaperAdvancedSettings` is the page block's share of the advanced tier: half
 * page, timed test, negative numbers, the worked key and the differentiation
 * set. ADR 0023 §4 kept these next to the page count; the workbench gathers
 * every advanced control into one section at the foot of the rail instead
 * (ADR 0061 §3), so the two halves are rendered in different places.
 *
 * The share button used to sit here. It is an action on the finished sheet, so
 * it moved to the bar above the preview.
 */
export const PaperSettings = React.memo(function PaperSettings() {
    const {getSettings, setSettings} = useSettingsActions();
    const {isSpiralReview, numberOfPages, answerPages} = useSetting((settings) => ({
        isSpiralReview: settings.Format === ProblemFormatOptions.SpiralReview,
        numberOfPages: settings.NumberOfPages,
        answerPages: settings.AnswerPages,
    }));
    const t = useTranslation();

    function handlePagesChange(e) {
        let value = parseInt(e.value);
        if (value > 50) {
            value = 50;
        }

        let updatedSettings = {...getSettings()};
        updatedSettings.NumberOfPages = isNaN(value) ? 1 : value;
        setSettings(updatedSettings);
    }

    function handleAnswerPagesToggle(value) {
        let updatedSettings = {...getSettings()};
        updatedSettings.AnswerPages = value;
        setSettings(updatedSettings);
    }

    return (
        <div className='paperSettings'>
            {/* A spiral-review sheet's section counts are the count;
                the per-page control would only confuse (ADR 0013). */}
            {!isSpiralReview && (
                <div className='field'>
                    <span className='fieldLabel'>{t('workbench.problemsPerPage')}</span>
                    <NumberOfProblems/>
                </div>
            )}
            <div className='paperRow'>
                <div className='field pagesField'>
                    <label htmlFor='numberOfPages'>{t('workbench.pagesLabel')}</label>
                    <InputNumber inputId='numberOfPages' className='pagesInput' value={numberOfPages} onChange={handlePagesChange} showButtons
                        pt={{
                            incrementButton: { 'aria-label': t('settings.addPageAria') },
                            decrementButton: { 'aria-label': t('settings.removePageAria') },
                        }}/>
                </div>
                <div className='answerPagesContainer'>
                    <label htmlFor='answerPages'>{t('workbench.answerKey')}</label>
                    <ToggleButton onLabel={t('settings.toggleYes')} offLabel={t('settings.toggleNo')} inputId='answerPages' className="answerPagesButton" pt={TOGGLE_PT} checked={answerPages} onChange={(e) => handleAnswerPagesToggle(e.value)}  />
                </div>
            </div>
        </div>
    );
});

export const PaperAdvancedSettings = React.memo(function PaperAdvancedSettings() {
    const {getSettings, setSettings} = useSettingsActions();
    const view = useSetting((settings) => ({
        showsHalfPage: showsHalfPage(settings),
        showsFactLevels: showsFactLevels(settings),
        showsTimed: showsTimed(settings),
        showsAllowNegatives: showsAllowNegatives(settings),
        showsWorkedAnswers: showsWorkedAnswers(settings),
        halfPage: settings.HalfPage,
        timed: settings.Timed,
        factLevel: settings.FactLevel ?? 0,
        allowNegatives: settings.AllowNegatives,
        workedAnswers: settings.WorkedAnswers,
        differentiationSet: settings.DifferentiationSet,
    }));
    const t = useTranslation();

    function handleAllowNegativesToggle(value) {
        let updatedSettings = {...getSettings()};
        updatedSettings.AllowNegatives = value;
        setSettings(updatedSettings);
    }

    function handleTimedToggle(value) {
        let updatedSettings = {...getSettings()};
        updatedSettings.Timed = value;
        setSettings(updatedSettings);
    }

    // Turning levels on starts at level 1 and leaves the value ranges alone,
    // so turning them off gives the visitor's ranges back (ADR 0071 §5).
    // Turning them off also puts the sheet back to a test, so a practice sheet does not wait
    // inside the setting for the next time levels come on (ADR 0071 §8).
    function handleFactLevelsToggle(value) {
        setSettings({ ...getSettings(), FactLevel: value ? 1 : 0, FactLevelSheet: 'test' });
    }

    function handleDifferentiationToggle(value) {
        let updatedSettings = {...getSettings()};
        updatedSettings.DifferentiationSet = value;
        setSettings(updatedSettings);
    }

    function handleWorkedAnswersToggle(value) {
        setSettings({ ...getSettings(), WorkedAnswers: value });
    }

    function handleHalfPageToggle(value) {
        let updatedSettings = {...getSettings()};
        updatedSettings.HalfPage = value;
        updatedSettings.NumberOfProblemsPerPage = 21;
        setSettings(updatedSettings);
    }

    return (
        <div className='paperAdvancedSettings'>
            {view.showsHalfPage && (
                <div className='answerPagesContainer'>
                    <label htmlFor='halfPage'>{t('settings.halfPageLabel')}</label>
                    <ToggleButton onLabel={t('settings.toggleYes')} offLabel={t('settings.toggleNo')} inputId='halfPage' className="answerPagesButton" checked={view.halfPage} onChange={(e) => handleHalfPageToggle(e.value)}  />
                </div>
            )}
            {view.showsTimed && (
                <div className='answerPagesContainer'>
                    <label htmlFor='timed'>{t('settings.timedTestLabel')}</label>
                    <ToggleButton onLabel={t('settings.toggleYes')} offLabel={t('settings.toggleNo')} inputId='timed' className="answerPagesButton" checked={view.timed} onChange={(e) => handleTimedToggle(e.value)}  />
                </div>
            )}
            {/* A ladder of level tests (ADR 0071 §5). The level itself is
                picked in the value panel, which it replaces while this is on. */}
            {view.showsFactLevels && (
                <div className='answerPagesContainer'>
                    <label htmlFor='factLevels'>{t('settings.factLevelsLabel')}</label>
                    <ToggleButton onLabel={t('settings.toggleYes')} offLabel={t('settings.toggleNo')} inputId='factLevels' className="answerPagesButton" checked={view.factLevel > 0} onChange={(e) => handleFactLevelsToggle(e.value)}  />
                </div>
            )}
            {/* Only two formats can take negatives, and both are horizontal -
                the column layout has no place to put a sign (ADR 0036 §2,
                ADR 0030 §2). */}
            {view.showsAllowNegatives && (
                <div className='answerPagesContainer'>
                    <label htmlFor='allowNegatives'>{t('settings.negativeNumbersLabel')}</label>
                    <ToggleButton onLabel={t('settings.toggleYes')} offLabel={t('settings.toggleNo')} inputId='allowNegatives' className="answerPagesButton" checked={view.allowNegatives} onChange={(e) => handleAllowNegativesToggle(e.value)}  />
                </div>
            )}
            {/* Show the working on the key, not just the answer. ADR 0015. */}
            {view.showsWorkedAnswers && (
                <div className='answerPagesContainer'>
                    <label htmlFor='workedAnswers'>{t('settings.showWorkingLabel')}</label>
                    <ToggleButton onLabel={t('settings.toggleYes')} offLabel={t('settings.toggleNo')} inputId='workedAnswers' className="answerPagesButton" checked={view.workedAnswers} onChange={(e) => handleWorkedAnswersToggle(e.value)}  />
                </div>
            )}
            {/* Three ability tiers of this sheet in one PDF - one step easier,
                as configured, one step harder. ADR 0010. */}
            <div className='answerPagesContainer'>
                <label htmlFor='differentiationSet'>{t('settings.differentiationSetLabel')}</label>
                <ToggleButton onLabel={t('settings.toggleYes')} offLabel={t('settings.toggleNo')} inputId='differentiationSet' className="answerPagesButton" checked={view.differentiationSet} onChange={(e) => handleDifferentiationToggle(e.value)}  />
            </div>
        </div>
    );
});

/**
 * Both halves together. `showAdvanced` defaults to true so that a caller which
 * knows nothing about the tiers - a test, or any future page that mounts this
 * panel on its own - gets the whole panel.
 */
export function PageSettings({ showAdvanced = true }: { showAdvanced?: boolean }) {
    return (
        <div className='pageSettings'>
            <PaperSettings/>
            {showAdvanced && <PaperAdvancedSettings/>}
        </div>
    );
}
